import DOMPurify from 'dompurify'

const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'PRE', 'HR'])
const ALLOWED_TAGS = [
    'p',
    'br',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'del',
    'strike',
    'blockquote',
    'a',
    'hr',
    'pre',
    'code',
    'div',
    'span',
]

/** Older API rows may contain the two characters backslash+n instead of a line break. */
export function normalizePlainText(value: string): string {
    return value.replace(/\\r\\n|\\n|\\r/g, '\n').replace(/\r\n?/g, '\n')
}

function decodeTextEntities(value: string): string {
    // Escaping literal angle brackets ensures entity-encoded markup stays text.
    const document = new DOMParser().parseFromString(value.replace(/</g, '&lt;').replace(/>/g, '&gt;'), 'text/html')
    return document.body.textContent ?? ''
}

function escapeText(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function plainTextToHtml(value: string): string {
    const decoded = decodeTextEntities(value)
    if (!decoded.trim()) return ''
    return decoded
        .split(/\n[\t ]*\n/)
        .map((paragraph) => `<p>${escapeText(paragraph).replace(/\n/g, '<br>')}</p>`)
        .join('')
}

function repairLegacyAttributes(value: string): string {
    // Only unescape quotes inside tags; backslashes in normal prose stay intact.
    return value.replace(/<[^>]+>/g, (tag) => tag.replace(/\\(["'])/g, '$1'))
}

function isSafeHref(href: string): boolean {
    const compact = Array.from(href)
        .filter((character) => {
            const code = character.charCodeAt(0)
            return code > 32 && (code < 127 || code > 159)
        })
        .join('')
    return (
        !compact.startsWith('//') &&
        !compact.includes('\\') &&
        (!/^[a-z][a-z0-9+.-]*:/i.test(compact) || /^(https?:|mailto:|tel:)/i.test(compact))
    )
}

function wrapInlineChildren(parent: ParentNode & Node, document: Document): void {
    let paragraph: HTMLParagraphElement | null = null
    Array.from(parent.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((node as Element).tagName)) {
            paragraph = null
            return
        }
        if (!paragraph && node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) {
            node.parentNode?.removeChild(node)
            return
        }
        if (!paragraph) {
            paragraph = document.createElement('p')
            parent.insertBefore(paragraph, node)
        }
        paragraph.appendChild(node)
    })
}

/** Convert legacy content to a small, safe set of formatting supported by the editor. */
export function normalizeRichText(value: string): string {
    const normalized = normalizePlainText(value).trim()
    if (!normalized) return ''
    if (!/<\/?[a-z][^>]*>/i.test(normalized)) return plainTextToHtml(normalized)

    const clean = DOMPurify.sanitize(repairLegacyAttributes(normalized), {
        ALLOWED_TAGS,
        ALLOWED_ATTR: ['href', 'title', 'start'],
        ALLOW_DATA_ATTR: false,
        ALLOW_ARIA_ATTR: false,
    })
    const document = new DOMParser().parseFromString(clean, 'text/html')

    document.body.querySelectorAll('a').forEach((link) => {
        const href = link.getAttribute('href')
        if (href && !isSafeHref(href)) link.removeAttribute('href')
    })
    document.body.querySelectorAll('[start]').forEach((element) => {
        if (element.tagName !== 'OL' || !/^\d+$/.test(element.getAttribute('start') ?? '')) {
            element.removeAttribute('start')
        }
    })

    // Divs and spans from the old form become normal paragraphs and inline text.
    Array.from(document.body.querySelectorAll('div, span'))
        .reverse()
        .forEach((element) => {
            if (element.tagName === 'DIV') wrapInlineChildren(element, document)
            element.replaceWith(...Array.from(element.childNodes))
        })

    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT)
    const textNodes: Text[] = []
    let node = walker.nextNode()
    while (node) {
        textNodes.push(node as Text)
        node = walker.nextNode()
    }
    textNodes.forEach((text) => {
        if (!text.data.includes('\n') || text.parentElement?.closest('pre, code')) return
        if (!text.data.trim()) {
            const parentTag = text.parentElement?.tagName
            if (parentTag === 'BODY' || parentTag === 'UL' || parentTag === 'OL' || parentTag === 'BLOCKQUOTE') {
                text.remove()
                return
            }
        }
        const fragment = document.createDocumentFragment()
        text.data.split('\n').forEach((line, index) => {
            if (index) fragment.appendChild(document.createElement('br'))
            fragment.appendChild(document.createTextNode(line))
        })
        text.replaceWith(fragment)
    })
    wrapInlineChildren(document.body, document)
    return document.body.innerHTML
}

/** A readable text version for summaries and short, unformatted fields. */
export function richTextToPlainText(value: string): string {
    const document = new DOMParser().parseFromString(normalizeRichText(value), 'text/html')
    const read = (node: Node): string => {
        if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ''
        if (node.nodeType !== Node.ELEMENT_NODE) return ''
        const element = node as Element
        if (element.tagName === 'BR') return '\n'
        if (element.tagName === 'HR') return '\n\n'
        const content = Array.from(node.childNodes).map(read).join('')
        if (element.tagName === 'LI') return `${content.trim()}\n`
        if (BLOCK_TAGS.has(element.tagName)) return `${content.trim()}\n\n`
        return content
    }
    return Array.from(document.body.childNodes)
        .map(read)
        .join('')
        .replace(/\u00a0/g, ' ')
        .replace(/[\t ]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

export function hasRichTextContent(value: string): boolean {
    return (
        richTextToPlainText(value)
            .replace(/[\u200b-\u200d\ufeff]/g, '')
            .trim().length > 0
    )
}
